import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import { fmtEuro, fmtDate, chipPastel } from "components/sacrificesConfig";

const defaultForm = {
  libelle: "",
  date_debut: "",
  date_fin: "",
  prix_ovins_caprins: "",
  prix_vache: "",
  prix_chameau: "",
};

function SacrificeSessions() {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const sessionActive = sessions.find((s) => s.statut === "active");

  const fetchSessions = async () => {
    try {
      const res = await api.get("/sacrifice-sessions");
      setSessions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchSessions(); }, []);

  const handleOpen = () => {
    setForm(defaultForm);
    setError("");
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!form.libelle) {
      setError("Le libellé est obligatoire");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/sacrifice-sessions", form);
      fetchSessions();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleCloturer = async (session) => {
    if (!window.confirm(`Clôturer la session "${session.libelle}" ?\nPlus aucune demande ne pourra être saisie.`)) return;
    try {
      await api.put("/sacrifice-sessions/" + session.id + "/cloturer");
      fetchSessions();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur serveur");
    }
  };

  const ligneprix = (label, valeur) => (
    <MDBox display="flex" justifyContent="space-between">
      <MDTypography variant="caption" color="text">{label}</MDTypography>
      <MDTypography variant="caption" fontWeight="bold">{fmtEuro(valeur)}</MDTypography>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">Sessions de sacrifices</MDTypography>
                <MDButton
                  variant="outlined" color="white" size="small"
                  onClick={handleOpen} disabled={!!sessionActive}
                  title={sessionActive ? "Une session est déjà active" : ""}
                >
                  <Icon>add</Icon>&nbsp;Nouvelle session
                </MDButton>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                {sessionActive && (
                  <MDTypography variant="caption" color="text" display="block" mb={2}>
                    Une session est active : clôturez-la pour pouvoir en créer une nouvelle.
                  </MDTypography>
                )}
                <Grid container spacing={3}>
                  {sessions.map((s) => (
                    <Grid item xs={12} md={6} lg={4} key={s.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee", height: "100%" }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="h6">{s.libelle}</MDTypography>
                          <Chip
                            label={s.statut === "active" ? "Active" : "Clôturée"}
                            size="small"
                            sx={chipPastel(s.statut === "active" ? "#43a047" : "#7b809a")}
                          />
                        </MDBox>
                        <MDTypography variant="caption" color="text" display="block" mb={1}>
                          Du {fmtDate(s.date_debut)} au {fmtDate(s.date_fin)} · {s.nb_demandes} demande(s)
                        </MDTypography>
                        <MDBox sx={{ bgcolor: "#f8f9fa", borderRadius: "8px", p: 1.5 }}>
                          {ligneprix("Ovins / Caprins", s.prix_ovins_caprins)}
                          {ligneprix("Vache", s.prix_vache)}
                          {ligneprix("Chameau", s.prix_chameau)}
                        </MDBox>
                        {s.statut === "active" && (
                          <MDButton
                            size="small" color="error" variant="outlined" fullWidth sx={{ mt: 2 }}
                            onClick={() => handleCloturer(s)}
                          >
                            <Icon>lock</Icon>&nbsp;Clôturer
                          </MDButton>
                        )}
                      </Card>
                    </Grid>
                  ))}
                  {sessions.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune session. Cliquez sur "Nouvelle session".
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Nouvelle session de sacrifices</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Libellé" fullWidth
              value={form.libelle}
              onChange={(e) => setForm({ ...form, libelle: e.target.value })}
            />
            <MDBox display="flex" gap={2}>
              <TextField
                label="Date de début" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.date_debut}
                onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
              />
              <TextField
                label="Date de fin" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.date_fin}
                onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
              />
            </MDBox>
            <MDBox display="flex" gap={2}>
              <TextField
                label="Prix ovins/caprins €" type="number" fullWidth
                value={form.prix_ovins_caprins}
                onChange={(e) => setForm({ ...form, prix_ovins_caprins: e.target.value })}
              />
              <TextField
                label="Prix vache €" type="number" fullWidth
                value={form.prix_vache}
                onChange={(e) => setForm({ ...form, prix_vache: e.target.value })}
              />
              <TextField
                label="Prix chameau €" type="number" fullWidth
                value={form.prix_chameau}
                onChange={(e) => setForm({ ...form, prix_chameau: e.target.value })}
              />
            </MDBox>
            {error && (
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleSubmit} color="info" disabled={loading}>
            {loading ? "Enregistrement..." : "Créer"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default SacrificeSessions;
